import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  Address,
  IBankDetails,
  IERechnungsInfo,
  IFormModel,
  IInvoiceConfig,
  IOlavEntry,
  IVertragskonto,
  RECHNUNGSVERSAND_ERECHNUNG,
  RECHNUNGSVERSAND_POST,
  ZAHLUNGSMETHODE_BANKEINZUG,
  ZAHLUNGSMETHODE_ZAHLSCHEIN,
} from '@mwe/models';
import { InternationalAddressInputComponent } from '../../address-input/international-address-input.component';
import { IBANFormComponent } from '../../iban-form/iban-form.component';
import { Validators } from '@angular/forms';
import { equalEmailValidator, getAddressGroupFormatted, getEmailValidatorWithRegExp } from '@mwe/utils';
import { FormComponent } from '../../form/form.component';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { BasicCardTitleComponent } from '../../cards/basic-card/basic-card-title/basic-card-title.component';
import { TranslateService } from '@ngx-translate/core';
import { InfoboxComponent } from '../../infobox/infobox.component';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';

@Component({
  selector: 'mwe-invoice-information',
  templateUrl: './invoice-information.component.html',
  imports: [
    BasicCardComponent,
    BasicCardTitleComponent,
    FormComponent,
    IBANFormComponent,
    InternationalAddressInputComponent,
    InfoboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceInformationComponent {
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly invoiceInfoConfig = input<IInvoiceConfig>();
  readonly isTitleVisible = input<boolean>(true);
  readonly isSepaOrZahlscheinSliderVisible = input<boolean | 'readonly'>(true);
  readonly isERechnungVisible = input<boolean>(true);
  readonly isERechnungSliderVisible = input<boolean>(true);
  readonly isInternationalAddressInputVisible = input<boolean>(false);
  readonly newInvoiceAddressTitle = input<string>('invoiceInformation.newInvoiceAddress.title');
  readonly allowEmailChange = input<boolean>(true);
  readonly productCategories = input<string[]>([]);
  readonly showAddressChangeInfo = input<boolean>(false);

  readonly onSetInvoiceData = output<IVertragskonto>();
  readonly onSetErechnungData = output<IERechnungsInfo>();
  readonly onIBANFormSuccess = output<IBankDetails>();
  readonly onIBANFormError = output<any>();

  /** ================== Signals =================== */

  readonly ibanFormRef = viewChild(IBANFormComponent);
  readonly emailFormRef = viewChild<FormComponent>('emailForm');
  readonly paymentFormRef = viewChild<FormComponent>('paymentForm');
  readonly invoiceFormRef = viewChild<FormComponent>('invoiceForm');
  readonly internationalAddressInputRef = viewChild(InternationalAddressInputComponent);

  readonly overrideEmailAddress = signal<string | undefined>(undefined);
  readonly emailAddressFormValid = signal<boolean>(undefined);

  readonly toggleInvoiceLabel = computed(() =>
    this.invoiceSEPA() ? 'invoiceInformation.invoiceHow.toggleSepa.on' : 'invoiceInformation.invoiceHow.toggleSepa.off',
  );
  readonly toggleERechnungLabel = computed(() =>
    this.erechnungEnabled() ? 'invoiceInformation.erechnung.toggleERechnung.on' : 'invoiceInformation.erechnung.toggleERechnung.off',
  );

  readonly isSepaPflicht = linkedSignal<boolean>(() => this.invoiceInfoConfig()?.invoiceMandatory ?? false);
  readonly isERechnungsPflicht = linkedSignal<boolean>(() => this.invoiceInfoConfig()?.eRechnungMandatory ?? false);
  readonly invoiceData = linkedSignal({
    source: () => this.invoiceInfoConfig()?.invoiceData,
    computation: data => {
      const baseData = data ?? {
        vertragskontoId: null,
        blz: null,
        kontonummer: null,
        zahlungsmethode: null,
        iban: null,
      };
      const forceSepa = this.isSepaPflicht() || (!!baseData.iban && baseData.zahlungsmethode !== ZAHLUNGSMETHODE_BANKEINZUG);

      return forceSepa ? { ...baseData, zahlungsmethode: ZAHLUNGSMETHODE_BANKEINZUG } : baseData;
    },
    equal: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  });
  readonly erechnungData = linkedSignal({
    source: () => ({
      data: this.invoiceInfoConfig()?.erechnungData,
      mail: this.invoiceInfoConfig()?.rechnungsMail,
    }),
    computation: source => {
      const baseData = {
        isERechnungsEmpfaenger: source.data?.isERechnungsEmpfaenger ?? true,
        rechnungEmail: source.mail ?? source.data?.rechnungEmail,
      };

      if (this.isERechnungsPflicht()) return { ...baseData, isERechnungsEmpfaenger: true };
      return baseData;
    },
    equal: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  });
  readonly ibanConfig = linkedSignal({
    source: () => this.invoiceData()?.iban,
    computation: iban => ({
      iban,
      ibanPlaceholderKey: 'ibanForm.iban.placeholder',
      ibanLabelKey: 'ibanForm.iban.label',
    }),
    equal: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  });
  readonly address = linkedSignal<string>(() => this.invoiceInfoConfig()?.address);
  readonly invoiceSEPA = linkedSignal<boolean>(
    () => this.isSepaPflicht() || this.invoiceData()?.zahlungsmethode === ZAHLUNGSMETHODE_BANKEINZUG,
  );
  readonly erechnungEnabled = linkedSignal<boolean>(() => {
    return this.isERechnungsPflicht() || (this.erechnungData()?.isERechnungsEmpfaenger ?? false);
  });

  readonly translatedToggleInvoiceLabel = toSignal(
    toObservable(this.toggleInvoiceLabel).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(label => this.translateService.get(label)),
    ),
  );
  readonly translatedToggleERechnungLabel = toSignal(
    toObservable(this.toggleERechnungLabel).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(label => this.translateService.get(label)),
    ),
  );

  readonly emailAddressForm = computed<IFormModel>(() => this.createEmailAddressForm());
  readonly readonlyAddressForm = computed<IFormModel>(() => this.createReadonlyAddressForm());
  readonly shouldShowEmailChangeForm = computed(() => this.allowEmailChange() && (this.erechnungEnabled() || this.isERechnungsPflicht()));
  readonly paymentSelectionForm = computed<IFormModel>(() => this.createPaymentSelectionForm());
  readonly readonlyPaymentSelectionForm = computed<IFormModel>(() => this.createReadonlyPaymentSelectionForm());
  readonly showPaymentSelectForm = computed(() => this.paymentOptions()?.length > 1 && this.isSepaOrZahlscheinSliderVisible() === true);
  readonly invoiceSelectionForm = computed<IFormModel>(() => this.createInvoiceSelectionForm());
  readonly readonlyInvoiceSelectionForm = computed<IFormModel>(() => this.createReadonlyInvoiceSelectionForm());
  readonly showInvoiceSelectForm = computed(() => this.invoiceOptions()?.length > 1 && this.isERechnungSliderVisible());
  readonly addressChangeWarningPlaceholders = computed(() => ({ contact: this.invoiceInfoConfig()?.invoiceAddressChangesTo }));
  readonly paymentOptions = computed(() =>
    this.isSepaPflicht() ? [ZAHLUNGSMETHODE_BANKEINZUG] : [ZAHLUNGSMETHODE_BANKEINZUG, ZAHLUNGSMETHODE_ZAHLSCHEIN],
  );
  readonly invoiceOptions = computed(() => {
    const options = [RECHNUNGSVERSAND_POST];
    if (this.isERechnungsPflicht()) return [RECHNUNGSVERSAND_ERECHNUNG];
    if (this.isERechnungVisible()) options.push(RECHNUNGSVERSAND_ERECHNUNG);
    return options;
  });
  readonly showInvoiceEmailInfo = computed<boolean>(() => !!this.invoiceInfoConfig()?.customerDataChangeEmailMsgKey);
  readonly showInvoiceAddressChangeWarning = computed<boolean>(
    () =>
      !this.showAddressChangeInfo() &&
      this.erechnungEnabled() &&
      !!this.invoiceInfoConfig()?.invoiceAddressChangesTo &&
      !this.isERechnungsPflicht(),
  );

  /** ================== Effects =================== */

  constructor() {
    effect(() => {
      if (this.isInternationalAddressInputVisible() && this.internationalAddressInputRef()) {
        untracked(() => this.checkAddress());
      }
    });

    effect(() => {
      const data = this.invoiceData();
      if (!data) return;

      const inputData = untracked(() => this.invoiceInfoConfig()?.invoiceData);
      if (JSON.stringify(data) !== JSON.stringify(inputData)) {
        untracked(() => this.onSetInvoiceData.emit(data));
      }
    });

    effect(() => {
      const isValid = this.emailAddressFormValid();
      const erechnungData = this.erechnungData();
      const overrideEmail = this.overrideEmailAddress();

      if (!erechnungData) return;

      const result = {
        ...erechnungData,
        rechnungEmail: overrideEmail ?? erechnungData.rechnungEmail,
      };

      if (isValid !== false) {
        untracked(() => this.onSetErechnungData.emit(result));
      }
    });

    effect(() => {
      const paymentMethod = this.invoiceData()?.zahlungsmethode ?? ZAHLUNGSMETHODE_BANKEINZUG;
      const paymentForm = this.paymentFormRef();
      if (!paymentForm?.form) return;

      const paymentControl = paymentForm.form.get('paymentMethod');
      if (!paymentControl || paymentControl.value === paymentMethod) return;

      paymentControl.patchValue(paymentMethod, { emitEvent: false });
    });
  }

  /** ================== Template and Utility Functions =================== */

  async validate(): Promise<void> {
    const validationResults: boolean[] = [];
    if (this.showPaymentSelectForm()) {
      this.paymentFormRef().fireValidation();
      validationResults.push(this.paymentFormRef().isValid());
    }

    if (this.showInvoiceSelectForm()) {
      this.invoiceFormRef().fireValidation();
      validationResults.push(this.invoiceFormRef().isValid());
    }

    if (this.shouldShowEmailChangeForm()) {
      this.emailFormRef().fireValidation();
      validationResults.push(this.emailFormRef().isValid());
    }

    if (this.isInternationalAddressInputVisible()) validationResults.push(this.validateAddress());

    if (this.invoiceSEPA()) await this.ibanFormRef().validateForm();

    if (!validationResults.every(e => e)) throw new Error('Form is not valid');
  }

  checkAddress(): void {
    this.address.set(getAddressGroupFormatted(this.getAddress(), true));
  }

  onPaymentFormChange({ paymentMethod }): void {
    const isSepa = paymentMethod === ZAHLUNGSMETHODE_BANKEINZUG;
    if (this.showPaymentSelectForm() && isSepa !== this.invoiceSEPA()) {
      this.onToggleIBAN(isSepa);
    }
  }

  onInvoiceFormChange({ invoiceMethod }): void {
    const isERechnung = invoiceMethod === 'eRechnung';
    if (this.showInvoiceSelectForm() && isERechnung !== this.erechnungEnabled()) {
      this.onToggleERechnung(isERechnung);
    }
  }

  onEmailFormChange({ email }, isValid: boolean): void {
    if (isValid !== this.emailAddressFormValid() || email !== this.overrideEmailAddress()) {
      this.emailAddressFormValid.set(isValid);
      this.overrideEmailAddress.set(isValid ? email : undefined);
    }
  }

  onToggleIBAN(checked: boolean): void {
    this.invoiceSEPA.set(checked);
    this.invoiceData.update(inv => {
      if (!checked) {
        if (inv.zahlungsmethode === ZAHLUNGSMETHODE_ZAHLSCHEIN && inv.iban === null && inv.bic === null) return inv;
        return { ...inv, zahlungsmethode: ZAHLUNGSMETHODE_ZAHLSCHEIN, iban: null, bic: null };
      }

      if (inv.zahlungsmethode === ZAHLUNGSMETHODE_BANKEINZUG) return inv;
      return { ...inv, zahlungsmethode: ZAHLUNGSMETHODE_BANKEINZUG };
    });
  }

  onToggleERechnung(checked: boolean): void {
    this.erechnungEnabled.set(checked);
    this.erechnungData.update(inv => {
      if (inv.isERechnungsEmpfaenger === checked) return inv;
      return { ...inv, isERechnungsEmpfaenger: checked };
    });
  }

  ibanFormError(data?: any): void {
    this.onIBANFormError.emit(data);
  }

  ibanFormSubmitted(data: IBankDetails): void {
    this.invoiceData.update(inv => {
      if (inv.iban === data.iban) return inv;
      return { ...inv, iban: data.iban };
    });
    this.onIBANFormSuccess.emit(data);
  }

  async validateForm(): Promise<void> {
    await this.ibanFormRef()?.validateForm();
  }

  validateAddress(): boolean {
    return this.internationalAddressInputRef()?.validate();
  }

  getAddress(): Address {
    return this.internationalAddressInputRef()?.getAddress();
  }

  getOlavEntry(): IOlavEntry {
    return this.internationalAddressInputRef()?.getOlavEntry();
  }

  /** ================== Form Create Functions =================== */

  createEmailAddressForm(): IFormModel {
    const erechnungData = this.erechnungData();
    const shouldShowEmailChangeForm = this.shouldShowEmailChangeForm();

    return {
      inputs: [
        {
          dataType: 'email',
          name: 'email',
          initialValue: erechnungData?.rechnungEmail || '',
          validators: [Validators.required, Validators.email, getEmailValidatorWithRegExp()],
          labelKey: 'invoiceInformation.email.label',
          validate: true,
          validationErrorLabelKey: 'invoiceInformation.email.validationError',
          componentType: 'emailToConfirm',
          updateOn: 'change',
          isReadonly: !shouldShowEmailChangeForm,
          emailConfirmationOptions: { referenceEmailIsInitialValue: true },
        },
        shouldShowEmailChangeForm && {
          dataType: 'email',
          name: 'emailConfirmation',
          initialValue: '',
          validators: [Validators.required, equalEmailValidator('email')],
          labelKey: 'invoiceInformation.email.confirmationLabel',
          validate: true,
          validationErrorLabelKey: 'invoiceInformation.email.confirmationError',
          avoidCopyPaste: true,
          isHidden: true,
          componentType: 'emailConfirmationField',
          updateOn: 'change',
        },
      ],
    };
  }

  private createReadonlyAddressForm(): IFormModel {
    const address = this.address();
    if (!address) return undefined;

    return {
      inputs: [
        {
          name: 'address',
          initialValue: address,
          labelKey: 'invoiceInformation.invoiceOptions.postal',
          isReadonly: true,
        },
      ],
    };
  }

  private createPaymentSelectionForm(): IFormModel {
    const isSepaOrZahlscheinSliderVisible = this.isSepaOrZahlscheinSliderVisible();
    const initialValue = this.invoiceData()?.zahlungsmethode ?? ZAHLUNGSMETHODE_BANKEINZUG;

    return {
      inputs: [
        isSepaOrZahlscheinSliderVisible === 'readonly' && {
          name: 'paymentMethod',
          initialValue,
          validators: [Validators.required],
          labelKey: 'invoiceInformation.paymentOptions.title',
          isReadonly: true,
        },
        isSepaOrZahlscheinSliderVisible !== 'readonly' && {
          name: 'paymentMethod',
          initialValue,
          options: this.paymentOptions()?.map(option => ({
            value: option,
            text: `invoiceInformation.paymentOptions.${option}`,
          })),
          componentType: 'select',
          validators: [Validators.required],
          labelKey: 'invoiceInformation.paymentOptions.title',
        },
      ],
    };
  }

  private createReadonlyPaymentSelectionForm(): IFormModel {
    const translatedToggleInvoiceLabel = this.translatedToggleInvoiceLabel();

    return {
      inputs: [
        {
          name: 'readonlyPaymentMethod',
          labelKey: 'invoiceInformation.paymentOptions.title',
          initialValue: translatedToggleInvoiceLabel,
          isReadonly: true,
        },
      ],
    };
  }

  private createInvoiceSelectionForm(): IFormModel {
    const erechnungEnabled = this.erechnungEnabled();
    const invoiceOptions = this.invoiceOptions();

    return {
      inputs: [
        {
          name: 'invoiceMethod',
          initialValue: erechnungEnabled ? 'eRechnung' : 'Post',
          labelKey: 'invoiceInformation.invoiceOptions.title',
          options: invoiceOptions?.map(option => ({
            value: option,
            text: `invoiceInformation.invoiceOptions.${option}`,
          })),
          componentType: 'select',
          validators: [Validators.required],
        },
      ],
    };
  }

  private createReadonlyInvoiceSelectionForm(): IFormModel {
    const translatedToggleERechnungLabel = this.translatedToggleERechnungLabel();

    return {
      inputs: [
        {
          name: 'readonlyInvoiceMethod',
          labelKey: 'invoiceInformation.invoiceOptions.title',
          initialValue: translatedToggleERechnungLabel,
          isReadonly: true,
        },
      ],
    };
  }
}
